import { CALIBRATION_TIERS, type StoredCalibration } from '../graphics-calibration.js';
import { formatNumber, intlLocale, t, tc } from '../i18n/index.js';
import type { GraphicsQuality } from '../settings.js';
import { escapeHtml } from './html.js';

/**
 * The performance-test summary shared by the menu's GRAPHICS popover and
 * Settings › Video: what was measured, when, and a one-click re-run.
 */

const PRESETS: readonly GraphicsQuality[] = ['auto', ...CALIBRATION_TIERS];

const GAUGE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4.5 16.5a8 8 0 1 1 15 0"/><path d="m12 13 4-5"/><circle cx="12" cy="13" r="1.4"/></svg>';
const SLIDERS_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h10M18 7h2M4 17h4M12 17h8"/><circle cx="16" cy="7" r="2"/><circle cx="10" cy="17" r="2"/></svg>';
const RETRY_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.4-5.7"/><path d="M20 4v5h-5"/></svg>';

export function graphicsTestCardHtml(
  calibration: StoredCalibration | null,
  options: { disabledReason?: string; quality?: GraphicsQuality } = {},
): string {
  const measured = calibration?.status === 'measured' ? calibration : null;
  const custom = measured && options.quality && options.quality !== 'auto' && options.quality !== measured.tier ? options.quality : null;
  const title = custom
    ? t('YOUR CHOICE: {tier}', { tier: t(custom.toUpperCase()) })
    : measured
    ? t('TESTED: {tier}', { tier: t(measured.tier.toUpperCase()) })
    : calibration?.status === 'skipped' ? t('TEST SKIPPED') : t('NOT TESTED YET');
  const detail = custom && measured
    ? t('Recommended: {tier} · GPU {gpu}', { tier: t(measured.tier.toUpperCase()), gpu: formatNumber(measured.gpuScore) })
    : measured
    ? t('{fps} FPS expected · GPU {gpu} · {date}', {
      fps: measured.estimatedFps,
      gpu: formatNumber(measured.gpuScore),
      date: new Date(measured.measuredAt).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short' }),
    })
    : t('A short test drive picks the best quality your device holds at 60 FPS.');
  const badge = measured ? t((custom ?? measured.tier).toUpperCase()).slice(0, 1) : '?';
  return `
    <div class="graphics-test-card" data-measured="${Boolean(measured)}">
      <span class="graphics-test-badge" aria-hidden="true">${badge}</span>
      <span class="graphics-test-copy"><b>${escapeHtml(title)}</b><small title="${escapeHtml(measured?.renderer ?? '')}">${escapeHtml(detail)}</small></span>
      <span class="graphics-test-buttons">
        ${measured ? `<button type="button" class="graphics-test-run graphics-test-adjust" data-graphics-adjust ${options.disabledReason ? `disabled title="${escapeHtml(options.disabledReason)}"` : ''}>${SLIDERS_ICON}${t('ADJUST')}</button>` : ''}
        <button type="button" class="graphics-test-run" data-graphics-test ${options.disabledReason ? `disabled title="${escapeHtml(options.disabledReason)}"` : ''}>${RETRY_ICON}${measured ? t('TEST AGAIN') : t('RUN TEST')}</button>
      </span>
    </div>`;
}

export function graphicsQuickHtml(calibration: StoredCalibration | null, quality: GraphicsQuality): string {
  const measured = calibration?.status === 'measured' ? calibration : null;
  const label = quality === 'auto' ? (measured ? t(measured.tier.toUpperCase()) : t('AUTO')) : t(quality.toUpperCase());
  return `
    <details class="graphics-quick" id="graphics-quick">
      <summary title="${t('Graphics and performance test')}" aria-label="${t('Graphics and performance test')}">${GAUGE_ICON}<span>${tc('video', 'GRAPHICS')}</span><em>${escapeHtml(label)}</em></summary>
      <div class="graphics-quick-panel">
        ${graphicsTestCardHtml(calibration, { quality })}
        <div class="graphics-presets" role="radiogroup" aria-label="${t('QUALITY PRESET')}">
          <span>${t('QUALITY PRESET')}</span>
          <div>${PRESETS.map((preset) => `<button type="button" role="radio" aria-checked="${preset === quality}" data-graphics-preset="${preset}">${preset === 'auto' ? (measured ? t('TESTED') : 'AUTO') : t(preset.toUpperCase())}</button>`).join('')}</div>
        </div>
      </div>
    </details>`;
}
